<template>
  <CustomForm ref="form" :inputs=inputs :submit=buttons.submit style="max-width: 400px; margin: auto;">
    <CustomSelectField :content=inputs.defaultQuoteValidityPeriod />
    <CustomSelectField :content=inputs.defaultInvoicePaiementPeriod />
    <CustomTextField :content=inputs.defaultUnitPrice />
    <CustomTextField :content=inputs.defaultTva />
    <div class="vat-regime">
      <CustomSelectField :content=inputs.subjectToVat />
      <p v-if="isRegimeChanged" class="vat-hint">
        Dès l'enregistrement, {{ inputs.subjectToVat.value === 'NO' ? "les documents générés portent la mention « TVA non applicable », les achats sont comptés TTC dans les marges (la TVA des fournisseurs n'est pas récupérable) et la comptabilité ne déduit plus de TVA" : "les documents générés n'ont plus la mention de TVA non applicable, et les marges et la comptabilité des achats repassent en HT" }}.
        Les documents et achats antérieurs au changement ne sont pas modifiés.
      </p>
      <ul v-if="history.length > 0" class="vat-history">
        <li v-for="entry in history" :key="entry.from">Depuis le {{ formatDay(entry.from) }} : {{ entry.subjectToVat ? "assujetti à la TVA" : "sans TVA (franchise en base)" }}</li>
      </ul>
    </div>
    <CustomTextField :content=inputs.electronicAddress />
    <div class="tva-period-container">
      <CustomTextField :content=inputs.defaultTvaStartPeriodDate />
      <CustomTextField :content=inputs.defaultTvaEndPeriodDate />
    </div>
    <CustomTextAreaField style="height: 100px" :content=inputs.defaultBank />
    <p class="legal-title">Mentions légales des documents</p>
    <p class="vat-hint">Ces textes figurent en pied des factures, acomptes et avoirs (et dans la facture électronique). Le motif de TVA non applicable figure aussi sur les devis ; il n'apparaît que sur les documents datés d'une période où vous n'êtes pas assujetti à la TVA.</p>
    <CustomTextAreaField style="height: 70px" :content=inputs.vatExemptionNote />
    <CustomTextAreaField style="height: 70px" :content=inputs.paymentNote />
    <CustomTextAreaField style="height: 70px" :content=inputs.latePaymentNote />
    <CustomTextAreaField style="height: 70px" :content=inputs.discountNote />
    <CustomButton style="margin-top: 30px" @click="submitForm" :params=buttons.submit />
    <CustomButton @click="$refs.generateTokenForLinkAccountantModal.openModal()" :params=buttons.linkAccountant style="margin-top: 20px; width: 400px;" class="submit" />
  </CustomForm>
  <GenerateTokenForLinkAccountantModal ref="generateTokenForLinkAccountantModal"/>
</template>

<script>
    import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
    import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
    import router from "@/router";
    import {NotificationService} from "@vues/v-common";
    import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
    import CustomForm from "@/components/forms/CustomForm.vue";
    import {BusinessGroupParamsController} from "@/controllers/business/BusinessGroupParamsController";
    import CustomTextAreaField from "@/components/forms/customs/customTextAreaField/CustomTextAreaField.vue";
    import {TokenService} from "@/services/TokenService";
    import GenerateTokenForLinkAccountantModal from "@/components/modals/GenerateTokenForLinkAccountantModal.vue";
    import {cleanVatRegimes, currentSubjectToVat} from "@/purchase/vatRegime";
    import {resetVatRegimes} from "@/purchase/services/VatRegimeService";

    // Mêmes textes par défaut que ms-business (services/documentLegalNotes.ts)
    const DEFAULT_LEGAL_NOTES = {
      vatExemptionNote: "TVA non applicable, art. 293 B du CGI",
      paymentNote: "En cas de retard de paiement, une indemnité forfaitaire de 40 € pour frais de recouvrement sera due.",
      latePaymentNote: "En cas de retard de paiement, des pénalités de retard seront appliquées au taux égal à trois fois le taux d'intérêt légal.",
      discountNote: "Escompte pour paiement anticipé : néant.",
    };

    export default {
        name: 'BusinessGroupParametersForm',
        components: {
          GenerateTokenForLinkAccountantModal,
          CustomTextAreaField,
          CustomForm,
          CustomSelectField,
          CustomButton,
          CustomTextField,
        },
        data: function () {
            return {
                options: [],
                inputs: {
                  electronicAddress: {label: 'Address electronique', value: ''},
                  defaultTvaStartPeriodDate: {label: 'Début de la période', value: '', type: 'date'},
                  defaultTvaEndPeriodDate: {label: 'Fin de la période', value: '', type: 'date'},
                  defaultBank: {label: 'Coordonnées bancaires', value: ''},
                  defaultQuoteValidityPeriod: {label: 'Période de validité (jours)', value: '', type: 'select', selectValues: [{label:"45", value: 45}, {label:"30", value: 30}, {label:"15", value: 15}]},
                  defaultInvoicePaiementPeriod: {label: 'Délai de paiement (jours)', value: '', type: 'select', selectValues: [{label:"45", value: 45}, {label:"30", value: 30}, {label:"15", value: 15}, {label:"A la réception", value: 0}]},
                  defaultUnitPrice: {label: 'Prix unitaire (euros)', value: '', type: 'number'},
                  defaultTva: {label: 'TVA par défaut (%)', value: '', type: 'number'},
                  subjectToVat: {label: 'Gestion de la TVA', value: 'YES', type: 'select', selectValues: [{label: "Assujetti à la TVA", value: 'YES'}, {label: "Sans TVA (franchise en base)", value: 'NO'}]},
                  vatExemptionNote: {label: 'Motif de TVA non applicable', value: ''},
                  paymentNote: {label: 'Indemnité de recouvrement', value: ''},
                  latePaymentNote: {label: 'Pénalités de retard', value: ''},
                  discountNote: {label: 'Escompte', value: ''},
                },
                // Historique daté du régime de TVA (jamais réécrit) et régime en vigueur
                history: [],
                currentSubjectToVat: true,
                buttons:{
                  submit: {text: "Mettre à jour"},
                  linkAccountant : { text : "Lier un comptable" }
                },
                dropDownTabOpen: false,
            }
        },
        computed: {
          // Le régime choisi diffère de celui en vigueur : une date d'effet est nécessaire
          isRegimeChanged() {
            return (this.inputs.subjectToVat.value === 'YES') !== this.currentSubjectToVat;
          },
        },
        async mounted(){
          this.$refs.form?.startLoading();
          await this.load();
          this.$refs.form?.stopLoading();
        },
      methods: {
        // Instant d'un changement de régime (jour et heure)
        formatDay(instant) {
          return new Date(instant).toLocaleString('fr-FR', {dateStyle: 'short', timeStyle: 'short'});
        },
        async load() {
          const resp = await BusinessGroupParamsController.get(localStorage.groupId)
          if(!resp.errors){
            this.history = cleanVatRegimes(resp.vatRegimes);
            this.currentSubjectToVat = currentSubjectToVat(this.history);
            this.inputs.subjectToVat.value = this.currentSubjectToVat ? 'YES' : 'NO';
            this.options = resp.options;
            this.inputs.electronicAddress.value = resp.electronicAddress;
            this.inputs.defaultTvaStartPeriodDate.value = resp.defaultTvaStartPeriodDate;
            this.inputs.defaultTvaEndPeriodDate.value = resp.defaultTvaEndPeriodDate;
            this.inputs.defaultBank.value = resp.defaultBank;
            this.inputs.defaultQuoteValidityPeriod.value = resp.defaultQuoteValidityPeriod;
            this.inputs.defaultInvoicePaiementPeriod.value = resp.defaultInvoicePaiementPeriod;
            this.inputs.defaultUnitPrice.value = resp.defaultUnitPrice;
            this.inputs.defaultTva.value = resp.defaultTva;
            // Texte légal par défaut tant que le groupe n'en a pas enregistré un
            Object.keys(DEFAULT_LEGAL_NOTES).forEach(key => { this.inputs[key].value = resp[key] || DEFAULT_LEGAL_NOTES[key]; });
          }
        },
        async submitForm() {
          const res = await this.$refs.form.submitForm(() => BusinessGroupParamsController.patch(localStorage.groupId, {
            electronicAddress: this.inputs.electronicAddress.value,
            defaultTvaStartPeriodDate: this.inputs.defaultTvaStartPeriodDate.value,
            defaultTvaEndPeriodDate: this.inputs.defaultTvaEndPeriodDate.value,
            defaultBank: this.inputs.defaultBank.value,
            defaultQuoteValidityPeriod: this.inputs.defaultQuoteValidityPeriod.value,
            defaultInvoicePaiementPeriod: this.inputs.defaultInvoicePaiementPeriod.value,
            defaultUnitPrice: this.inputs.defaultUnitPrice.value,
            defaultTva: this.inputs.defaultTva.value,
            vatExemptionNote: this.inputs.vatExemptionNote.value,
            paymentNote: this.inputs.paymentNote.value,
            latePaymentNote: this.inputs.latePaymentNote.value,
            discountNote: this.inputs.discountNote.value,
            // Un changement de régime ajoute une entrée à l'historique (instant enregistré par le serveur) ; sans changement, rien n'est envoyé
            ...(this.isRegimeChanged ? {subjectToVat: this.inputs.subjectToVat.value === 'YES'} : {})
          }));
          if (!res.errors) {
            resetVatRegimes();
            await this.load();
            (new NotificationService).addNotification("success","Paramètres de groupe modifié");
            TokenService.renewToken();
            router.push("/group-parameters");
          }
        }
      }
    }
</script>

<style lang="stylus" scoped>
  @require '~/src/common/style/common.styl';

  .separator
    margin-top 15px
    height 2px
    background-color THEME_COLOR_PRIMARY_FADDED
    opacity 0.6

  .tva-period-container
    display flex
    gap 15px

  .legal-title
    margin 18px 0 2px
    font-weight 600
    color #444

  .vat-hint
    margin 4px 0 10px
    font-size 13px
    color #6b7280

  .vat-history
    margin 4px 0 10px
    padding-left 18px
    font-size 13px
    color #6b7280

  .options-container
    .line
      display flex
      align-items center
      .line1
        width 15px
        height 1px
        background-color #d1d1d4
      .line2
        height 1px
        background-color #d1d1d4
        width 100%
      .title
        color #444
        font-weight 600
        margin 0
        margin-bottom 5px
        margin-top 5px
        padding-left 5px
        padding-right 5px
    .option-container
      border solid 1px
      margin-top 5px
      margin-bottom 5px
      border-color #d1d1d4
      background-color #f8f8f8
      .option-name
        text-align center
        margin 5px
        color #a8a8a8
        font-weight 600
        font-size 15px
</style>
